import React from 'react';

/**
 * Loading/waiting illustration: two flat gears, very light grey and
 * semi-transparent, turning in opposite directions forever. No props --
 * this is the bare artwork, and every caller puts its own copy and its own
 * sizing around it (see GearLoadingState for the pairing used across the
 * app's wait states).
 *
 * The piece is already approved art, arrived at over several iterations.
 * Two things about how it is built that look like they want tidying up and
 * must NOT be tidied up:
 *
 * 1. The 0.6 opacity is applied ONCE, to the `gl-fade-all` group wrapping
 *    both gears together. Moving it onto each piece (body, teeth) or onto
 *    each gear separately would darken every place they overlap -- tooth
 *    over body, one gear over the other -- because two semi-transparent
 *    layers add up where they meet. One group, one fade.
 *
 * 2. The white centre circles sit OUTSIDE both `gl-fade-all` and the two
 *    rotating groups, on purpose. A circle looks identical however much it
 *    spins, so there is nothing to gain by rotating them -- and staying
 *    outside the fade is what keeps them solid white instead of picking up
 *    the 0.6 the rest of the drawing carries.
 *
 * The only thing changed from the approved source is the `gl-` prefix on
 * the class and keyframe names: a `<style>` block inside inline SVG is
 * global to the document, not scoped to this element, so names as common
 * as `spin`, `fade-all` or `gf` would leak out over the whole app and be
 * leaked into by it. Geometry, colours, timings and structure are
 * untouched -- it renders pixel for pixel as approved.
 */
const GearLoader: React.FC = () => (
  <svg width="100%" viewBox="0 0 680 320" role="img">
    <title>Animación de carga: dos engranajes gris muy claro y fino con agujero central blanco girando en sentidos opuestos</title>
    <desc>
      Icono plano de dos engranajes gris muy claro y semitransparente, uno grande y uno pequeño, con agujero blanco central,
      girando en sentidos contrarios de forma continua
    </desc>
    <style>{`
      .gl-gear1 { transform-origin: 290px 180px; animation: gl-spin 2.5s linear infinite; }
      .gl-gear2 { transform-origin: 395px 131px; animation: gl-spinrev 1.8s linear infinite; }
      @keyframes gl-spin { to { transform: rotate(360deg); } }
      @keyframes gl-spinrev { to { transform: rotate(-360deg); } }
      .gl-fade-all { opacity: 0.6; }
      .gl-gf { fill: #e2e2e2; }
    `}</style>
    <g className="gl-fade-all">
      <g className="gl-gear1">
        <circle className="gl-gf" cx="290" cy="180" r="80" />
        <rect className="gl-gf" x="275" y="72" width="30" height="40" rx="8" transform="rotate(0 290 180)" />
        <rect className="gl-gf" x="275" y="72" width="30" height="40" rx="8" transform="rotate(45 290 180)" />
        <rect className="gl-gf" x="275" y="72" width="30" height="40" rx="8" transform="rotate(90 290 180)" />
        <rect className="gl-gf" x="275" y="72" width="30" height="40" rx="8" transform="rotate(135 290 180)" />
        <rect className="gl-gf" x="275" y="72" width="30" height="40" rx="8" transform="rotate(180 290 180)" />
        <rect className="gl-gf" x="275" y="72" width="30" height="40" rx="8" transform="rotate(225 290 180)" />
        <rect className="gl-gf" x="275" y="72" width="30" height="40" rx="8" transform="rotate(270 290 180)" />
        <rect className="gl-gf" x="275" y="72" width="30" height="40" rx="8" transform="rotate(315 290 180)" />
      </g>
      <g className="gl-gear2">
        <circle className="gl-gf" cx="395" cy="131" r="44" />
        <rect className="gl-gf" x="386" y="73" width="18" height="22" rx="5" transform="rotate(0 395 131)" />
        <rect className="gl-gf" x="386" y="73" width="18" height="22" rx="5" transform="rotate(60 395 131)" />
        <rect className="gl-gf" x="386" y="73" width="18" height="22" rx="5" transform="rotate(120 395 131)" />
        <rect className="gl-gf" x="386" y="73" width="18" height="22" rx="5" transform="rotate(180 395 131)" />
        <rect className="gl-gf" x="386" y="73" width="18" height="22" rx="5" transform="rotate(240 395 131)" />
        <rect className="gl-gf" x="386" y="73" width="18" height="22" rx="5" transform="rotate(300 395 131)" />
      </g>
    </g>
    <circle cx="290" cy="180" r="30" fill="#ffffff" />
    <circle cx="395" cy="131" r="16" fill="#ffffff" />
  </svg>
);

export default GearLoader;
